import "core-js/modules/web.dom-collections.iterator.js";
/* eslint-disable react/destructuring-assignment */
import React, { useState, useMemo, useEffect } from 'react';
import { styled } from '../styles';
import FlexLoading from "../FlexLoading";
import ErrorBoundary from '../ErrorBoundary';
import Box from "../Box";
import Paper from "../Paper";
import ListSearchFilters from "../ListSearchFilters";

// eslint-disable-next-line import/no-cycle
import ListHeader from '../ListHeader';
import ListFooter from '../ListFooter';
export * from './ListBase.types';
export const isLayoutVariantCompact = layoutVariant => {
  return layoutVariant == "compact-narrow";
};

//deal with overflow of shadow
const overflowStyleShadowMarginNoPadding = {
  marginLeft: "-8px",
  marginRight: "-8px",
  marginBottom: "-8px",
  marginTop: "-8px",
  paddingLeft: "8px",
  paddingRight: "8px",
  paddingTop: "8px",
  paddingBottom: "8px"
};
const ListRootContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'filterVariant'
})(_ref => {
  let {
    filterVariant = "drawer",
    theme
  } = _ref;
  return {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    //important for flex grow shrink
    flexDirection: filterVariant == 'drawer' ? 'column' : 'row',
    ...overflowStyleShadowMarginNoPadding
  };
});
const ListPanelFilterContainer = styled(Box)(_ref2 => {
  let {
    theme
  } = _ref2;
  return {
    maxWidth: 600,
    minWidth: 300,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    //important for flex grow shrink
    ...overflowStyleShadowMarginNoPadding,
    marginRight: theme.spacing(1) //actualy half because of overflowStyleShadowMarginNoPadding
  };
});
const ListPanelMainContainer = styled(Box)(_ref3 => {
  let {
    theme
  } = _ref3;
  return {
    margin: theme.spacing(0, 2),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    //important for flex grow shrink
    ...overflowStyleShadowMarginNoPadding
  };
});
const HeaderContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'compact'
})(_ref4 => {
  let {
    compact = false,
    theme
  } = _ref4;
  return {
    paddingBottom: theme.spacing(compact == false ? 1.5 : 1)
  };
});
const ViewContextContainer = styled(Box, {
  shouldForwardProp: prop => ["compact", "layoutVariant"].indexOf(String(prop)) === -1
})(_ref5 => {
  let {
    compact = false,
    theme
  } = _ref5;
  return {
    paddingTop: theme.spacing(compact == false ? 2 : 0.5, 0),
    paddingBottom: theme.spacing(compact == false ? 2 : 0.5, 0),
    flex: 1,
    display: 'grid',
    overflow: 'auto',
    ...overflowStyleShadowMarginNoPadding
  };
});
const FooterContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'layoutVariant'
})(_ref6 => {
  let {
    compact = false,
    theme
  } = _ref6;
  return {
    paddingTop: theme.spacing(compact == false ? 1.5 : 1)
  };
});

/*
  TODO: discuss adding props.Viewcontext to dependency arrays
  not added because the user may not pass in ViewContexts as memoized
  and so every render of our parent component that occurs a new array would be passed in and trigger a rerender of the view context
  leaving the memoization of viewcontexts up to the user is tricky and obtuse for the user
  if we do not include props.viewcontexts in our dependency array our component will not rerender if our parent component has a dynamic viewcontext array
  as a short optimization we could provide the length of props.Viewcontexts but this does not solve all of our edge cases
*/
export function ListBase(_ref7) {
  var _props$searchFilters;
  let {
    filterVariant = "drawer",
    layoutVariant = "standard",
    highlightable = false,
    ...props
  } = _ref7;
  const {
    viewContexts,
    labelFilters = "Filters",
    labelApplyFilters = "Apply Filters",
    labelClearAllFilters = "Clear All",
    labelClose = "Close",
    labelClear = "Clear"
  } = props;
  if (layoutVariant == "compact-narrow" && filterVariant == "inline") {
    throw new Error("'compact-narrow' layoutVariant is not supported with 'inline' filterVariant");
  }
  if (layoutVariant == "compact-narrow" && viewContexts.length > 1) {
    throw new Error("'compact-narrow' layoutVariant can only support one view context");
  }
  const [selectedViewContext, setSelectedViewContext] = useState(0);
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(props.appliedSearchQuery);
  const [appliedSortOrder, setAppliedSortOrder] = useState(props.appliedSortOrder);
  const [appliedSearchFilters, setAppliedSearchFilters] = useState(props.appliedSearchFilters || []);
  const [triggerSearch, setTriggerSearch] = useState(0);
  const selectedIdsSet = useMemo(() => new Set((props.selectedIds || []).filter(id => typeof id === 'string')), [props.selectedIds]);
  const partiallySelectedIdsSet = useMemo(() => new Set((props.partiallySelectedIds || []).filter(id => typeof id === 'string')), [props.partiallySelectedIds]);
  useEffect(() => {
    if (appliedSortOrder !== props.appliedSortOrder) {
      setAppliedSortOrder(props.appliedSortOrder);
    }
  }, [props.appliedSortOrder]);
  useEffect(() => {
    if (Boolean(props.appliedSearchFilters) && appliedSearchFilters !== props.appliedSearchFilters) {
      setAppliedSearchFilters(props.appliedSearchFilters);
    }
  }, [props.appliedSearchFilters]);
  useEffect(() => {
    if (appliedSearchQuery !== props.appliedSearchQuery) {
      setAppliedSearchQuery(props.appliedSearchQuery);
    }
  }, [props.appliedSearchQuery]);
  const handleSelectedViewContextChange = viewContextIndex => {
    setSelectedViewContext(currentIndex => viewContextIndex === null ? currentIndex : viewContextIndex);
  };
  const handleSortChange = _ref8 => {
    let {
      newSortOrder
    } = _ref8;
    setAppliedSortOrder(newSortOrder);
    setTriggerSearch(triggerSearch + 1);
  };

  // TODO: pass in all of the props and callbacks needed, make sure to memoize them
  const viewContextProps = useMemo(() => ({
    getRowID: props.getRowID,
    nodeIdKey: props.nodeIdKey,
    appliedSortOrder,
    onSort: handleSortChange,
    sortOrders: props.sortOrders,
    results: props.results,
    selectable: props.selectable,
    selectedIdsSet,
    highlightable: highlightable,
    highlightedRowIdSet: props.highlightedRowIdSet,
    labelNoResults: props.labelNoResults,
    onSelection: props.onSelection,
    nodeSelectionIdKey: props.nodeSelectionIdKey,
    selectionType: props.selectionType || 'multiple',
    nodeContext: props.nodeContext || {},
    partiallySelectedIdsSet
  }), [appliedSortOrder, handleSortChange, props.sortOrders, props.results, props.selectable, selectedIdsSet, props.onSelection, props.labelNoResults, props.nodeSelectionIdKey, props.selectionType, props.nodeContext, partiallySelectedIdsSet]);
  const RenderedViewContext = useMemo(() => {
    if (props.resultsLoading === true) {
      if (viewContexts[selectedViewContext].renderLoading) {
        return viewContexts[selectedViewContext].renderLoading(viewContextProps);
      }
      return /*#__PURE__*/React.createElement(FlexLoading, {
        size: "large"
      });
    }
    if (Boolean(viewContexts) && viewContexts.length > 0) {
      return viewContexts[selectedViewContext].renderResults(viewContextProps);
    }
    return null;
  }, [viewContexts, selectedViewContext, viewContextProps, props.resultsLoading]);
  useEffect(() => {
    // note: useEffect sideEffect we dont want to perform a search the first time this loads
    if (triggerSearch > 0 && typeof props.onSearch === 'function') {
      const searchParams = {
        q: appliedSearchQuery,
        appliedSortOrder,
        appliedSearchFilters
      };
      props.onSearch(searchParams);
    }
  }, [triggerSearch]);
  const handleSearchQueryChange = query => {
    setAppliedSearchQuery(query);
    setTriggerSearch(triggerSearch + 1);
  };
  const onApplySearchFilterChanged = params => {
    setAppliedSearchFilters(params);
    setTriggerSearch(triggerSearch + 1);
  };
  const listSearchFiltersProps = {
    searchFilters: props.searchFilters,
    appliedSearchFilters,
    onSearchFilterApplied: params => onApplySearchFilterChanged(params),
    onClose: null,
    labelFilters,
    labelClose,
    labelClear,
    labelApplyFilters,
    labelClearAllFilters
  };
  const compact = useMemo(() => layoutVariant == "compact-narrow", [layoutVariant]);
  return /*#__PURE__*/React.createElement(ListRootContainer, {
    filterVariant: filterVariant
  }, filterVariant == "inline" && ((_props$searchFilters = props.searchFilters) === null || _props$searchFilters === void 0 ? void 0 : _props$searchFilters.length) > 0 && /*#__PURE__*/React.createElement(ListPanelFilterContainer, {
    sx: {}
  }, /*#__PURE__*/React.createElement(Paper, {
    sx: {
      flex: 1,
      overflow: 'hidden',
      display: 'flex'
    }
  }, /*#__PURE__*/React.createElement(ListSearchFilters, listSearchFiltersProps))), /*#__PURE__*/React.createElement(ListPanelMainContainer, null, /*#__PURE__*/React.createElement(HeaderContainer, {
    "data-qa": "ListBaseHeaderContainer",
    compact: compact
  }, /*#__PURE__*/React.createElement(ErrorBoundary, null, /*#__PURE__*/React.createElement(ListHeader, {
    results: props.results,
    appliedSearchQuery: appliedSearchQuery,
    appliedSortOrder: appliedSortOrder,
    sortOrders: props.sortOrders,
    viewContexts: viewContexts,
    showSearch: props.showSearch,
    labelSearch: props.labelSearch,
    labelResults: props.labelResults,
    labelSearchAria: props.labelSearchAria,
    selectedViewContext: selectedViewContext,
    searchFilters: filterVariant == "drawer" ? props.searchFilters : undefined,
    appliedSearchFilters: appliedSearchFilters,
    onChangeSelectedViewContext: handleSelectedViewContextChange,
    onSearchQueryChanged: handleSearchQueryChange,
    onSortChanged: handleSortChange,
    onSearchFilterApplied: onApplySearchFilterChanged,
    selectable: props.selectable,
    selectedIds: props.selectedIds,
    allSelected: props.allSelected,
    onSelectAllChange: props.onSelectAll,
    SelectedActionGroupProps: props.SelectedActionGroupProps,
    GlobalActionGroupProps: props.GlobalActionGroupProps,
    selectionType: props.selectionType,
    labelFilters: labelFilters,
    labelClose: labelClose,
    labelClear: labelClear,
    labelApplyFilters: labelApplyFilters,
    labelClearAllFilters: labelClearAllFilters,
    labelSortBy: props.labelSortBy,
    layoutVariant: layoutVariant,
    renderPageActions: props.renderPageActions
  }))), /*#__PURE__*/React.createElement(ViewContextContainer, {
    "data-qa": "ListBaseViewContextContainer",
    compact: compact
  }, /*#__PURE__*/React.createElement(ErrorBoundary, null, RenderedViewContext)), /*#__PURE__*/React.createElement(FooterContainer, {
    "data-qa": "ListBaseFooterContainer",
    compact: compact
  }, /*#__PURE__*/React.createElement(ErrorBoundary, {
    size: "medium"
  }, /*#__PURE__*/React.createElement(ListFooter, {
    results: {
      nodes: props.results.nodes,
      totalCount: props.results.totalCount,
      pagingContext: props.pagingContext
    },
    ListPaginatorProps: {
      labelFirstToLastOfTotal: props.labelFirstToLastOfTotal,
      labelTo: props.labelTo,
      labelOf: props.labelOf
    },
    onChange: props.onPageChanged,
    ListPerPageSelectProps: {
      perPageValue: props.resultsPerPage,
      perPageOptions: props.perPageOptions,
      onChange: props.onPaginationChange,
      labelPerPage: props.rowsPerPageLabel
    },
    layoutVariant: layoutVariant
  })))));
}
export default ListBase;